import { api, canWrite, send } from "../api";
import { nodeCards, nodeStateChip, stateChip } from "../parts";
import type { Enrol, NodeCard, Nodes } from "../types";
import { ago, chart, dayRange, days, field, figures, flash, form, gb, h, input, loadChip, nodeLink, num, rate, secret, select, show, size, statusChip, table, td, textarea, th, uptime } from "../ui";

/** "812 GB of 1 TB out since Oct 1 (billing day 1), 19 days left", or what's known. */
export function cycleLine(n: { quota_count: "out" | "both"; billing_day: number; capacity: { transfer: { used: number; allowance: number; startDay: string; left: number } } }) {
  const tr = n.capacity.transfer;
  const counted = n.quota_count === "both" ? "in and out" : "out";
  return `${gb(tr.used)}${tr.allowance ? ` of ${gb(tr.allowance)}` : ""} ${counted} since ${tr.startDay} (billing day ${n.billing_day}), ${tr.left} days left`;
}

export async function nodesPage() {
  let all = new URLSearchParams(location.search).get("all") === "1";
  const nodes = await api<Nodes>(`/v1/admin/nodes?days=${days()}${all ? "&all=1" : ""}`);
  if (!nodes) return;
  const dl = dayRange(nodes.from, nodes.to);
  const inRotation = nodes.nodes.filter((n) => n.node_state === "active" || n.node_state === "unregistered");
  const up = inRotation.filter((n) => n.state === "up");
  const sumOf = (k: "reservations" | "circuits" | "peers" | "rate_in" | "rate_out") => nodes.nodes.reduce((t, n) => t + n[k], 0);
  const series = nodes.nodes.map((n) => ({
    name: n.relay,
    label: n.relay,
    values: new Map(nodes.days.filter((d) => d.relay === n.relay).map((d) => [d.day, d.relay_in + d.relay_out])),
  }));
  const rows = nodes.nodes.map((n: NodeCard) =>
    h(
      "tr",
      null,
      h("td", null, nodeLink(n.relay), h("span", { class: "muted block" }, n.label || "—")),
      h("td", null, stateChip(n.state, n.reported_at), " ", nodeStateChip(n)),
      h("td", { title: n.capacity.advice }, loadChip(n.capacity), n.capacity.limit ? h("span", { class: "muted block" }, n.capacity.meters.find((m) => m.key === n.capacity.limit)?.label ?? "") : null),
      h("td", { class: "num", title: cycleLine(n) }, n.capacity.transfer.allowance ? `${n.capacity.transfer.used_pct ?? 0}%` : "—"),
      h("td", null, statusChip(n.key, n.key === "own" ? "signs with its own key" : "signs with the shared relay key")),
      td(num(n.reservations), "num"),
      td(num(n.circuits), "num"),
      td(num(n.peers), "num"),
      td(rate(n.rate_out + n.rate_in), "num"),
      td(size(n.relay_in + n.relay_out), "num"),
      td(num(n.accounts), "num"),
      td(uptime(n.uptime_s), "num"),
      td(n.version ?? "—"),
      td(`${n.mem_mb} MB`, "num"),
      td(ago(n.reported_at), "num"),
    ),
  );

  // Show retired nodes too (?all=1).
  const allBox = h("input", { type: "checkbox", id: "f-all" }) as HTMLInputElement;
  allBox.checked = all;
  allBox.addEventListener("change", () => {
    all = allBox.checked;
    const u = new URL(location.href);
    if (all) u.searchParams.set("all", "1");
    else u.searchParams.delete("all");
    history.replaceState(null, "", u);
    void nodesPage();
  });

  // Add a node: the record first, then the one-time install command.
  const f = {
    name: input({ placeholder: "FRA", maxlength: "6", required: "", pattern: "[A-Za-z][A-Za-z0-9]{1,5}", autocomplete: "off", title: "2 to 6 letters or digits, starting with a letter" }),
    label: input({ placeholder: "Frankfurt", maxlength: "40", required: "" }),
    country: input({ placeholder: "DE", maxlength: "2", pattern: "[A-Za-z]{2}", autocomplete: "off" }),
    provider: input({ placeholder: "Vultr", maxlength: "40" }),
    model: input({ placeholder: "1 vCPU, 1 GB", maxlength: "60" }),
    monthly_gb: input({ type: "number", min: "0", max: "1000000", placeholder: "1000" }),
    quota_count: select({}, [["out", "out only"], ["both", "in and out"]], "out"),
    billing_day: input({ type: "number", min: "1", max: "28", value: "1" }),
    link_mbps: input({ type: "number", min: "0", max: "1000000", placeholder: "1000" }),
    public_ips: textarea({ class: "mono", rows: "2", placeholder: "One per line" }),
    notes: textarea({ rows: "2", maxlength: "2000", placeholder: "Where the invoice goes, who has root, anything else." }),
  };
  const result = h("div");
  const addForm = form(
    "fields",
    async () => {
      const r = await send<{ node: { node_state: string }; enrol: Enrol }>("POST", "/v1/admin/nodes", {
        name: f.name.value.trim().toUpperCase(),
        label: f.label.value.trim(),
        country: f.country.value.trim().toUpperCase(),
        provider: f.provider.value.trim(),
        model: f.model.value.trim(),
        monthly_gb: Number(f.monthly_gb.value) || 0,
        quota_count: f.quota_count.value,
        billing_day: Number(f.billing_day.value) || 1,
        link_mbps: Number(f.link_mbps.value) || 0,
        public_ips: f.public_ips.value.split(/\s+/).map((s) => s.trim()).filter(Boolean),
        notes: f.notes.value,
      });
      const name = f.name.value.trim().toUpperCase();
      result.replaceChildren(
        secret(`${name} is added. Run this on the new machine as root:`, [r.enrol.command], `Shown once; the token expires at ${new Date(r.enrol.expires_at * 1000).toLocaleString()} (24 hours). If you lose it, open the node and issue a new enrol command.`),
        h("p", null, nodeLink(name, `Open ${name} →`)),
      );
      addForm.reset();
      flash(`${name} added. It appears here as pending until the install command runs.`);
    },
    field("Name", f.name),
    field("Label", f.label),
    field("Country (ISO code)", f.country),
    field("Provider", f.provider),
    field("Model", f.model),
    field("Transfer allowance, GB per month (0: none)", f.monthly_gb),
    field("Counts", f.quota_count),
    field("Billing day of the month", f.billing_day),
    field("Link, Mbit/s (0: unknown)", f.link_mbps),
    field("Public IPs", f.public_ips, true),
    field("Notes", f.notes, true),
    h("div", null, h("button", { class: "btn small primary", type: "submit" }, "Add the node")),
  );

  show(
    "Nodes",
    `Relay nodes, live from their last report (every minute), and their traffic for ${nodes.from} – ${nodes.to} (UTC).`,
    figures([
      ["Nodes up", `${up.length} of ${inRotation.length}`, inRotation.filter((n) => n.state === "down").map((n) => `${n.relay} down`).join(", ") || (inRotation.length ? "all reporting" : "none in rotation")],
      ["Out of rotation", String(nodes.nodes.filter((n) => n.node_state === "dark" || n.node_state === "pending").length), nodes.nodes.filter((n) => n.node_state === "dark" || n.node_state === "pending").map((n) => `${n.relay} ${n.node_state}`).join(", ") || "none"],
      ["Computers connected", num(sumOf("reservations")), "holding a reservation"],
      ["Relayed connections", num(sumOf("circuits")), "open now"],
      ["Devices connected", num(sumOf("peers")), "computers, phones, guests"],
      ["Throughput now", rate(sumOf("rate_in") + sumOf("rate_out")), "over the last minute"],
    ]),
    nodes.advice.length ? h("div", { class: "advice" }, ...nodes.advice.map((a) => h("p", null, a))) : h("p", { class: "muted small" }, "Every node has room. A node counts as busy at 60% of its fullest resource and full at 80%."),
    nodeCards(nodes),
    h("h2", null, "Traffic per day and node"),
    chart(dl, series, size),
    h("div", { class: "listbar" }, h("h2", null, "All nodes"), h("label", { class: "check", for: "f-all" }, allBox, " Show retired")),
    table(
      [th("Node"), th("State"), th("Load"), th("Transfer", "num"), th("Key"), th("Computers", "num"), th("Circuits", "num"), th("Devices", "num"), th("Now", "num"), th(`${days()} days`, "num"), th("Accounts", "num"), th("Uptime", "num"), th("Version"), th("Memory", "num"), th("Reported", "num")],
      rows,
      "No nodes.",
    ),
    canWrite() ? h("h2", null, "Add a node") : null,
    canWrite() ? h("p", { class: "muted small" }, "Creates the record and gives you a one-line install command for a fresh Linux machine (root, systemd). The gateway installs itself, enrols and gets its own key; the node joins rotation when it first reports.") : null,
    canWrite() ? addForm : null,
    canWrite() ? result : null,
  );
}
